import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavConfig } from '../../contexts/NavConfigContext';
import { UserRole } from '../../services/types';
import { Perspective, PERSPECTIVES, PERSPECTIVE_LABELS } from '../../nav/navTypes';
import { computeVisibleGroups, resolveActivePageOnSwitch } from '../../nav/navSelectors';
import { iconFor } from '../../nav/navIcons';
import { logout } from '../../services/auth';
import FeedbackFAB from '../FeedbackFAB';
import ErrorToast from '../ErrorToast';
import OfflineBanner from '../OfflineBanner';
import { feedbackApi } from '../../services/api';

const drawerWidth = 240;

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tutor, loading, hasRole } = useAuth();
  const { config, perspective, setPerspective } = useNavConfig();

  // The user's auth role drives per-page access filtering (admin-only pages stay
  // admin-only) — independent of which navigation perspective is shown.
  const role: UserRole = hasRole('admin') ? 'admin' : hasRole('operator') ? 'operator' : 'viewer';
  const visibleGroups = computeVisibleGroups(config, perspective, role);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handlePerspectiveChange = (next: Perspective) => {
    setPerspective(next);
    // If the open page isn't visible in the new perspective, jump to its first
    // visible page (handoff behaviour).
    const { route } = resolveActivePageOnSwitch(config, next, role, location.pathname);
    if (route) navigate(route);
    setMobileOpen(false);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  // Redirect to login if not authenticated
  if (!loading && !user) {
    window.location.href = '/login';
    return null;
  }

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Nupis Campus
        </Typography>
      </Toolbar>
      <Divider />
      <Tabs
        value={perspective}
        onChange={(_event, value) => handlePerspectiveChange(value as Perspective)}
        variant="fullWidth"
        aria-label="Rollen-Perspektive"
      >
        {PERSPECTIVES.map((p) => (
          <Tab key={p} value={p} label={PERSPECTIVE_LABELS[p]} sx={{ minWidth: 0 }} />
        ))}
      </Tabs>
      <Divider />
      {visibleGroups.map((group) => (
        <Box key={group.id} sx={{ pt: 1 }}>
          <Typography
            variant="overline"
            sx={{ display: 'block', px: 2, color: 'text.secondary', lineHeight: 2 }}
          >
            {group.name}
          </Typography>
          <List disablePadding>
            {group.items.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.route}
                  onClick={() => handleMenuClick(item.route)}
                >
                  <ListItemIcon>{iconFor(item.id)}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Schulungsportal
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {tutor && (
              <Typography
                data-testid="user-profile"
                variant="body2"
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                {tutor.name} ({tutor.role})
              </Typography>
            )}
            <IconButton
              onClick={handleUserMenuOpen}
              size="small"
              sx={{ ml: 2 }}
              aria-controls="user-menu"
              aria-haspopup="true"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {tutor?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
            >
              <MenuItem disabled>
                <Typography variant="body2">{tutor?.email || user?.userDetails}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  navigate('/settings');
                }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Einstellungen
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Abmelden
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <OfflineBanner />
        <Box sx={{ p: 3, flexGrow: 1 }}>
          <Outlet />
        </Box>
      </Box>
      <ErrorToast />
      <FeedbackFAB onSubmit={(feedback) => feedbackApi.create(feedback)} />
    </Box>
  );
}
